// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Chip } from '@mui/material';
import { ComponentProps } from 'react';
import { z } from 'zod';

// =============================================================================
// Schemas + types
// =============================================================================

export const fulfillmentOrderPhaseSchema = z.enum([
  'draft',
  'awaiting_3pl',
  'cancelled',
  'open',
  'shipped',
  'delivered',
]);
export type FulfillmentOrderPhase = z.infer<typeof fulfillmentOrderPhaseSchema>;

export const fulfillmentOrderSubstatusSchema = z.enum([
  'partial',
  'full',
  'overage',
]);
export type FulfillmentOrderSubstatus = z.infer<
  typeof fulfillmentOrderSubstatusSchema
>;

export const fulfillmentOrderStatusSchema = z.enum([
  'OPEN_PARTIAL',
  'OPEN_FULL',
  'OPEN_OVERAGE',
  'SHIPPED_PARTIAL',
  'SHIPPED_FULL',
  'SHIPPED_OVERAGE',
  'DELIVERED_PARTIAL',
  'DELIVERED_FULL',
  'DELIVERED_OVERAGE',
  'DRAFT',
  'AWAITING_3PL',
  'CANCELLED',
]);
export type FulfillmentOrderStatus = z.infer<
  typeof fulfillmentOrderStatusSchema
>;

export const fulfillmentOrderClosedAtSourceSchema = z.enum([
  'SHIPMENT_COMPLETE',
  'MANUAL_CLOSE',
  'THIRD_PARTY_FIN',
  'CANCELLED',
]);
export type FulfillmentOrderClosedAtSource = z.infer<
  typeof fulfillmentOrderClosedAtSourceSchema
>;

// =============================================================================
// Chip + phase config
// =============================================================================

type ChipColor = ComponentProps<typeof Chip>['color'];
type ChipVariant = ComponentProps<typeof Chip>['variant'] | 'text';

interface ChipConfig {
  color: ChipColor;
  variant: ChipVariant;
}

interface PhaseConfig {
  label: string;
  backendFilters: FulfillmentOrderStatus[];
  displayOrder: number;
  chipConfig: ChipConfig;
}

export const PHASE_CONFIG: Record<FulfillmentOrderPhase, PhaseConfig> = {
  draft: {
    label: 'Draft',
    backendFilters: ['DRAFT'],
    displayOrder: 0,
    chipConfig: { color: 'default', variant: 'text' },
  },
  awaiting_3pl: {
    label: 'Awaiting 3PL',
    backendFilters: ['AWAITING_3PL'],
    displayOrder: 1,
    chipConfig: { color: 'default', variant: 'filled' },
  },
  open: {
    label: 'Open',
    backendFilters: ['OPEN_PARTIAL', 'OPEN_FULL', 'OPEN_OVERAGE'],
    displayOrder: 2,
    chipConfig: { color: 'default', variant: 'filled' },
  },
  shipped: {
    label: 'Shipped',
    backendFilters: ['SHIPPED_PARTIAL', 'SHIPPED_FULL', 'SHIPPED_OVERAGE'],
    displayOrder: 3,
    chipConfig: { color: 'info', variant: 'filled' },
  },
  delivered: {
    label: 'Delivered',
    backendFilters: [
      'DELIVERED_PARTIAL',
      'DELIVERED_FULL',
      'DELIVERED_OVERAGE',
    ],
    displayOrder: 4,
    chipConfig: { color: 'primary', variant: 'filled' },
  },
  cancelled: {
    label: 'Cancelled',
    backendFilters: ['CANCELLED'],
    displayOrder: 5,
    chipConfig: { color: 'error', variant: 'outlined' },
  },
};

export const FO_MODIFIER_LABELS: Record<FulfillmentOrderSubstatus, string> = {
  partial: 'Partial',
  full: 'Full',
  overage: 'Overage',
};

export const FULFILLMENT_ORDER_CLOSED_AT_SOURCE_LABELS: Record<
  FulfillmentOrderClosedAtSource,
  string
> = {
  SHIPMENT_COMPLETE: 'Completed automatically after full shipment',
  MANUAL_CLOSE: 'Manually closed short',
  THIRD_PARTY_FIN: 'Closed from 3PL FIN',
  CANCELLED: 'Cancelled',
};

// =============================================================================
// Status → phase/modifier mapping
// =============================================================================

const STATUS_TO_PHASE: Record<FulfillmentOrderStatus, FulfillmentOrderPhase> =
  Object.fromEntries(
    Object.entries(PHASE_CONFIG).flatMap(([phase, config]) =>
      config.backendFilters.map((status) => [status, phase])
    )
  ) as Record<FulfillmentOrderStatus, FulfillmentOrderPhase>;

export const PHASES_WITH_MODIFIERS: ReadonlySet<FulfillmentOrderPhase> =
  new Set<FulfillmentOrderPhase>(['open', 'shipped', 'delivered']);

export interface ParsedStatus {
  phase: FulfillmentOrderPhase;
  modifier: FulfillmentOrderSubstatus | null;
}

export function parseStatus(status: FulfillmentOrderStatus): ParsedStatus {
  const phase = STATUS_TO_PHASE[status];
  if (!PHASES_WITH_MODIFIERS.has(phase)) return { phase, modifier: null };
  const parts = status.split('_');
  const suffix = parts[parts.length - 1].toLowerCase();
  const parsed = fulfillmentOrderSubstatusSchema.safeParse(suffix);
  return { phase, modifier: parsed.success ? parsed.data : null };
}

export function getModifierVariant(
  phase: FulfillmentOrderPhase,
  modifier: FulfillmentOrderSubstatus | null
): ChipVariant {
  if (modifier === 'partial') return 'outlined';
  return PHASE_CONFIG[phase].chipConfig.variant;
}

// Audit-log labels for a fulfillment order status. Partial states read as
// "Partially shipped"; full and overage states collapse to the plain phase
// ("Open", "Shipped").
export const FO_STATUS_AUDIT_LABELS: Record<FulfillmentOrderStatus, string> =
  Object.fromEntries(
    fulfillmentOrderStatusSchema.options.map((status) => {
      const parsed = parseStatus(status);
      const phaseLabel = PHASE_CONFIG[parsed.phase].label;
      if (parsed.modifier === 'partial') {
        return [status, `Partially ${phaseLabel.toLowerCase()}`];
      }
      return [status, phaseLabel];
    })
  ) as Record<FulfillmentOrderStatus, string>;

// Notification labels keep overage; audit labels collapse it.
export const FO_STATUS_NOTIFICATION_LABELS: Record<
  FulfillmentOrderStatus,
  string
> = Object.fromEntries(
  fulfillmentOrderStatusSchema.options.map((status) => {
    const parsed = parseStatus(status);
    const phaseLabel = PHASE_CONFIG[parsed.phase].label;
    if (parsed.modifier === 'partial') {
      return [status, `Partially ${phaseLabel.toLowerCase()}`];
    }
    if (parsed.modifier === 'overage') {
      return [
        status,
        `${phaseLabel} with ${FO_MODIFIER_LABELS.overage.toLowerCase()}`,
      ];
    }
    return [status, phaseLabel];
  })
) as Record<FulfillmentOrderStatus, string>;

// Audit-log labels for the "Source" close-reason property. Distinct from the
// progress-track tooltip copy (FULFILLMENT_ORDER_CLOSED_AT_SOURCE_LABELS).
export const FO_CLOSE_SOURCE_AUDIT_LABELS: Record<
  FulfillmentOrderClosedAtSource,
  string
> = {
  SHIPMENT_COMPLETE: 'Automatic after full shipment',
  MANUAL_CLOSE: 'User closed manually',
  THIRD_PARTY_FIN: '3PL closed order',
  CANCELLED: 'Cancelled',
};

// =============================================================================
// Phase progression
// =============================================================================

const TERMINAL_PHASES: readonly FulfillmentOrderPhase[] = [
  'delivered',
  'cancelled',
];

const PHASE_PROGRESSION: readonly FulfillmentOrderPhase[] =
  fulfillmentOrderPhaseSchema.options.filter(
    (p: FulfillmentOrderPhase) => p !== 'cancelled'
  );

export function getPhaseProgression(
  isThirdPartyEnabled: boolean
): FulfillmentOrderPhase[] {
  return isThirdPartyEnabled
    ? [...PHASE_PROGRESSION]
    : PHASE_PROGRESSION.filter((s) => s !== 'awaiting_3pl');
}

export function getTerminalPhases(): readonly FulfillmentOrderPhase[] {
  return TERMINAL_PHASES;
}

// =============================================================================
// Status tooltips
// =============================================================================

export const FO_ORDER_STATUS_TOOLTIPS: Record<FulfillmentOrderStatus, string> =
  {
    DRAFT:
      'This fulfillment order is in draft (no shipments have been opened against any line item)',
    AWAITING_3PL: 'This fulfillment order is awaiting an update from your 3PL',
    OPEN_PARTIAL:
      'This fulfillment order is partially open (shipments have been opened with less than the requested quantity against one or more line items)',
    OPEN_FULL:
      'This fulfillment order is fully open (shipments have been opened with exactly the requested quantity against all line items)',
    OPEN_OVERAGE:
      'This fulfillment order is open with an overage (shipments have been opened with the requested quantity on all line items, and more than the requested quantity on at least one line item)',
    SHIPPED_PARTIAL:
      'This fulfillment order is partially shipped (shipments have been shipped with less than the requested quantity against one or more line items)',
    SHIPPED_FULL:
      'This fulfillment order is fully shipped (shipments have been shipped with exactly the requested quantity against all line items)',
    SHIPPED_OVERAGE:
      'This fulfillment order is shipped with an overage (shipments have been shipped with the requested quantity on all line items, and more than the requested quantity on at least one line item)',
    DELIVERED_PARTIAL:
      'This fulfillment order is partially delivered (shipments have been delivered with less than the requested quantity against one or more line items)',
    DELIVERED_FULL:
      'This fulfillment order is fully delivered (shipments have been delivered with exactly the requested quantity against all line items)',
    DELIVERED_OVERAGE:
      'This fulfillment order is delivered with an overage (shipments have been delivered with the requested quantity on all line items, and more than the requested quantity on at least one line item)',
    CANCELLED: 'This fulfillment order is cancelled',
  };
